import React, { Children, Fragment } from 'react';
import { Comment, Dimmer, Icon } from 'semantic-ui-react';
import Moment from 'react-moment';

import Viewer from '@vidende/components/fields/TUIWrapperViewer';
import { Comment as IComment } from '@vidende/contracts';

interface ConversationCommentProps {
    comment: IComment;
    canEdit: boolean;
    onEdit: (event: any, comment: any) => void;
    onDelete: (event: any, comment: any, deleted: boolean) => void;
    onReply: (event: any, comment: any) => void;
    isCurrentUserCommentAuthor: boolean;
    readOnly?: boolean;
    children?: React.ReactNode;
    // App-specific avatar loader (the party-icon fetch is authenticated and
    // env-specific). The Client injects its apiTools-based Image; the Portal
    // omits it (no avatar). Kept out of @vidende/components to avoid an apiTools
    // reach — same inject-the-variant pattern as RichTextArea's EditorComponent.
    ImageComponent?: React.ComponentType<any>;
}

const ConversationComment = ({
    comment,
    canEdit,
    isCurrentUserCommentAuthor,
    onEdit,
    onDelete,
    onReply,
    readOnly = false,
    children,
    ImageComponent,
}: ConversationCommentProps) => {
    return (
        <Comment>
            {(comment.deleted && isCurrentUserCommentAuthor) ||
            !comment.deleted ? (
                <Fragment>
                    {comment.creator.party?.iconAttachmentId &&
                        ImageComponent && (
                            <ImageComponent
                                renderComponent={Comment.Avatar}
                                url={`Parties/${comment.creator.partyId}/icon`}
                            />
                        )}
                    <Comment.Content>
                        <Dimmer
                            active={comment.deleted && canEdit}
                            style={{
                                pointerEvents: 'none',
                                background: 'rgba(250, 252, 252, .85)',
                            }}
                        />
                        <Comment.Author as="a">
                            {comment.creator.firstName}{' '}
                            {comment.creator.lastName}{' '}
                            {comment.creator.party &&
                                `(${comment.creator.party.label})`}
                        </Comment.Author>
                        <Comment.Metadata>
                            <div>
                                <Moment>{comment.createdAt.toString()}</Moment>
                            </div>
                        </Comment.Metadata>
                        <Comment.Text>
                            <Viewer initialValue={comment.content} />
                        </Comment.Text>
                        <Comment.Actions>
                            {!comment.deleted && (
                                <Comment.Action
                                    onClick={(
                                        event: React.MouseEvent<HTMLAnchorElement>,
                                    ) => onReply(event, comment)}
                                >
                                    <Icon name="reply" />
                                    Reply
                                </Comment.Action>
                            )}
                            {!readOnly && canEdit && (
                                <Fragment>
                                    {!comment.deleted && (
                                        <Fragment>
                                            <Comment.Action
                                                onClick={(
                                                    event: React.MouseEvent<HTMLAnchorElement>,
                                                ) => onEdit(event, comment)}
                                            >
                                                <Icon name="edit" />
                                                Edit
                                            </Comment.Action>
                                        </Fragment>
                                    )}

                                    <Comment.Action
                                        onClick={(
                                            event: React.MouseEvent<HTMLAnchorElement>,
                                        ) =>
                                            onDelete(
                                                event,
                                                comment,
                                                !comment.deleted,
                                            )
                                        }
                                    >
                                        <Icon name="trash" />
                                        {comment.deleted
                                            ? 'Undelete'
                                            : 'Delete'}
                                    </Comment.Action>
                                </Fragment>
                            )}
                        </Comment.Actions>
                    </Comment.Content>
                </Fragment>
            ) : (
                '[comment deleted]'
            )}

            {children}
        </Comment>
    );
};

export default ConversationComment;
